// extendRegistry (base list + one package's settings.json) and resolve (the layering of contract section 3).
// Pure functions on plain data. Neither throws on bad input: what is wrong is reported and left out.
import { REGISTRY, freeze } from './registry.js';
import { CONTROLS, LAYOUTS, LOOKS, AURORA_LEVELS, NEEDS, NO_BUTTON } from './controls.js';
import { readPackage, readStore, seasonMerged, isLegacy } from './legacy.js';
import { isObj, clone, at, check, monthDay, SEASONS, RE, valuesOnly, gateFor, offOf } from './values.js';
/** @typedef {import('./typedefs.js').Registry} Registry */
/** @typedef {import('./typedefs.js').SettingsJson} SettingsJson */
/** @typedef {import('./typedefs.js').Layers} Layers */
/** @typedef {import('./typedefs.js').ResolveResult} ResolveResult */

const SETTING_DEFAULTS = { control: null, gate: null, tour: false, placement: false, legacy: null, level: 'preset', group: 'look', apply: 'rebuild', edit: 'client' };
const CONTROL_DEFAULTS = { group: 'map', slot: 'controls', url: [], store: [], settings: [], parent: null };
const TYPES = ['bool', 'enum', 'number', 'text', 'color', 'monthDay', 'place', 'view', 'ids', 'list', 'doc'];
const FIELDS = { level: ['resort', 'preset'], group: ['look', 'show', 'content', 'visitor', 'camera', 'tours', 'language', 'studio'], apply: ['live', 'rebuild'], edit: ['client', 'studio'] };
// address parameters that choose what is loaded, the implicit preset, or a QA mode: no control may claim them (README)
const RESERVED = ['resort', 'preset', 'preview', 'edit', 'nodraft', 'sentry', 'embed', 'kiosk', 'debug', 'tm', 'ao', 'splat', 'cam'];
const X_KEY = /^x\.[a-z0-9][A-Za-z0-9.-]{0,60}$/, TOKEN = /^[a-z][a-z0-9-]{0,39}$/;
const strings = (a) => Array.isArray(a) && a.every((s) => typeof s === 'string' && s);

function build(settings, controls, looks, auroraLevels, layouts, strs, problems) {
  const byKey = new Map(), families = [], byControl = new Map();
  for (const e of settings) {
    if (e.family) families.push(e); else byKey.set(e.key, e);
    for (const a of e.aliases || []) byKey.set(a, e);
  }
  for (const c of controls) byControl.set(c.id, c);
  return freeze({
    settings, controls, looks, auroraLevels, layouts, strings: strs, problems,
    entry(key) {
      if (typeof key !== 'string') return null;
      const hit = byKey.get(key);
      if (hit) return hit;
      // a family member: `labels.<category id>` for the entry whose key is `labels.*`
      return families.find((f) => key.startsWith(f.key.slice(0, -1)) && RE.key.test(key.slice(f.key.length - 1))) || null;
    },
    control(id) { return byControl.get(id) || null; },
  });
}

/** The base list with no package additions (what `resolve` and the validators use when none is passed). */
export const BASE = build(REGISTRY, CONTROLS, LOOKS, AURORA_LEVELS, LAYOUTS, freeze({}), freeze([]));

/**
 * The base list plus one package's `settings.json`: its `x.` settings and controls, looks, aurora levels,
 * layouts and interface strings. Returns a new registry; the base is never changed. Every entry is checked
 * (shape, `x.` prefix, unique keys and ids, each address parameter and storage key claimed once, defaults and
 * `effects` that fit the type); a bad one is left out and named in `problems`.
 * `extendRegistry()` (or null) returns the base registry.
 * @param {SettingsJson | null} [settingsJson]
 * @returns {Registry}
 */
export function extendRegistry(settingsJson) {
  if (!isObj(settingsJson)) return BASE;
  const problems = [];
  const bad = (code, path, message) => { problems.push({ code, path, message }); };
  const list = (name) => (Array.isArray(settingsJson[name]) ? settingsJson[name] : []);
  if (settingsJson.schema !== 1) bad('bad-schema', 'schema', 'settings.json is schema 1');

  // tables first: an enum default or a layout may name a package look, level or control
  const byId = (base, name, ok) => {
    const out = base.map((x) => ({ ...x }));
    list(name).forEach((x, n) => {
      if (!isObj(x) || !TOKEN.test(x.id) || !ok(x)) return bad('bad-entry', `${name}[${n}]`, `"${x?.id}" is not a valid entry of ${name}`);
      const i = out.findIndex((o) => o.id === x.id);
      if (i >= 0) out[i] = { ...out[i], ...clone(x) }; else out.push({ label: `set.${name === 'looks' ? 'look.id' : 'aurora.level'}.${x.id}`, ...clone(x) });
    });
    return out;
  };
  const looks = byId(LOOKS, 'looks', (x) => x.grade === undefined || isObj(x.grade));
  const auroraLevels = byId(AURORA_LEVELS, 'auroraLevels', (x) => Number.isFinite(x.intensity) && x.intensity >= 0 && x.intensity <= 3);

  const settings = [...REGISTRY], controls = [...CONTROLS], pending = [];
  const taken = new Set(REGISTRY.flatMap((e) => [e.key, ...(e.aliases || [])]));
  list('settings').forEach((raw, i) => {
    const path = `settings[${i}]`;
    if (!isObj(raw) || typeof raw.key !== 'string') return bad('bad-entry', path, 'a setting needs a key');
    if (!X_KEY.test(raw.key)) return bad('not-x', path, `resort-specific key "${raw.key}" must start with "x."`);
    if (taken.has(raw.key)) return bad('duplicate-key', path, `key "${raw.key}" is declared twice`);
    const e = { ...SETTING_DEFAULTS, label: `set.${raw.key}`, ...clone(raw) };
    const wrong = !TYPES.includes(e.type) ? 'type' : Object.keys(FIELDS).find((f) => !FIELDS[f].includes(e[f]))
      || (e.type === 'enum' && !(strings(e.values) && e.values.length) ? 'values' : e.family ? 'family' : e.aliases !== undefined && !strings(e.aliases) ? 'aliases' : null);
    if (wrong) return bad('bad-entry', path, `"${raw.key}" has a bad "${wrong}"`);
    if (!('default' in raw) || !check(e, e.default, { looks, auroraLevels, layouts: LAYOUTS }).ok) return bad('bad-default', path, `"${raw.key}" needs a default that fits its type`);
    const values = e.type === 'enum' ? e.values : e.type === 'bool' ? ['true', 'false'] : null;
    if (e.effects !== undefined && !(isObj(e.effects) && Object.entries(e.effects).every(([v, p]) => (!values || values.includes(v)) && isObj(p)
      && Object.entries(p).every(([k, x]) => k.startsWith('_') || ((k === 'resort' || k === 'content') && isObj(x)))))) return bad('bad-effects', path, `"${raw.key}": effects are { <value>: { resort?, content? } }`);
    for (const k of [raw.key, ...(e.aliases || [])]) taken.add(k);
    pending.push([e, path]);
  });

  const ids = new Set(CONTROLS.map((c) => c.id)), params = new Set([...RESERVED, ...CONTROLS.flatMap((c) => c.url)]), stores = new Set(CONTROLS.flatMap((c) => c.store));
  list('controls').forEach((raw, i) => {
    const path = `controls[${i}]`;
    if (!isObj(raw) || typeof raw.id !== 'string') return bad('bad-entry', path, 'a control needs an id');
    if (!X_KEY.test(raw.id)) return bad('not-x', path, `resort-specific control "${raw.id}" must start with "x."`);
    if (ids.has(raw.id)) return bad('duplicate-id', path, `control "${raw.id}" is declared twice`);
    const c = { ...CONTROL_DEFAULTS, label: `ctl.${raw.id}`, ...clone(raw), legacy: null }; // a package control is never part of a legacy preset
    if (![c.url, c.store, c.settings].every((a) => Array.isArray(a) && a.every((s) => typeof s === 'string' && s))) return bad('bad-entry', path, `"${raw.id}": url, store and settings are lists of names`);
    const dup = (a, seen) => a.find((x, n) => seen.has(x) || a.indexOf(x) !== n);
    if (dup(c.url, params) !== undefined) return bad('param-taken', path, `address parameter "${dup(c.url, params)}" already belongs to another control`);
    if (dup(c.store, stores) !== undefined) return bad('store-taken', path, `storage key "${dup(c.store, stores)}" already belongs to another control`);
    const unknown = c.settings.find((k) => !taken.has(k));
    if (unknown !== undefined) return bad('unknown-key', path, `"${raw.id}" reaches "${unknown}", which is not a setting`);
    if (c.parent !== null && !ids.has(c.parent)) return bad('unknown-control', path, `"${raw.id}" is a part of "${c.parent}", which is not a control`);
    ids.add(c.id); c.url.forEach((p) => params.add(p)); c.store.forEach((s) => stores.add(s));
    controls.push(c);
  });
  for (const [e, path] of pending) {
    if (e.control !== null && !ids.has(e.control)) { bad('unknown-control', path, `"${e.key}" names the control "${e.control}", which does not exist`); e.control = null; }
    settings.push(e);
  }

  const layouts = { ...LAYOUTS };
  for (const [id, raw] of Object.entries(isObj(settingsJson.layout) ? settingsJson.layout : {})) {
    const path = `layout.${id}`, base = LAYOUTS[id] || LAYOUTS.default;
    if (!TOKEN.test(id) || !isObj(raw) || (raw.bodyClass != null && !TOKEN.test(raw.bodyClass)) || (raw.slots !== undefined && !isObj(raw.slots))) { bad('bad-entry', path, `layout "${id}" is { slots, bodyClass?, panel? }`); continue; }
    const slots = { ...base.slots };
    for (const [slot, order] of Object.entries(raw.slots || {})) {
      if (!Array.isArray(order)) { bad('bad-entry', `${path}.slots.${slot}`, 'a slot is an ordered list of control ids'); continue; }
      for (const c of order) if (!ids.has(c)) bad('unknown-control', `${path}.slots.${slot}`, `"${c}" is not a control`);
      slots[slot] = order.filter((c) => ids.has(c));
    }
    layouts[id] = { ...base, bodyClass: LAYOUTS[id] ? base.bodyClass : null, panel: LAYOUTS[id] ? base.panel : false, label: `set.layout.id.${id}`, ...clone(raw), id, slots };
  }
  const strs = {};
  for (const [lang, map] of Object.entries(isObj(settingsJson.strings) ? settingsJson.strings : {})) {
    if (!RE.lang.test(lang) || !isObj(map)) { bad('bad-entry', `strings.${lang}`, 'strings are { <language>: { <key>: text } }'); continue; }
    strs[lang] = Object.fromEntries(Object.entries(map).filter(([, s]) => typeof s === 'string'));
  }
  return build(settings, controls, looks, auroraLevels, layouts, strs, problems);
}

/**
 * VIS-75: the first need of control `id` (controls.js NEEDS) the settings leave unmet, or null: a control that does
 * nothing while they are as they are. `get` reads one setting's value. The engine (`shownOf`) and the portal's
 * editor (its reasons in words) ask the same table.
 * @param {string} id  @param {(key: string) => unknown} get
 * @returns {{ key: string, not?: unknown, min?: number, why: string } | null}
 */
export function controlBlocked(id, get) {
  for (const n of NEEDS[id] || []) {
    const v = get(n.key);
    if ('not' in n ? v === n.not : (Array.isArray(v) ? v.length : 0) < n.min) return n;
  }
  return null;
}

/**
 * Shown controls of a preset: its allowlist, known ids only, in registry order, parts of a hidden control dropped,
 * (VIS-75) and a control the owner's settings make useless (`controlBlocked` on `get`) dropped too.
 * A control guests never get a button for (controls.js NO_BUTTON) is never shown.
 * @param {Registry} reg
 * @param {{ show?: string[] } | null | undefined} preset
 * @param {((key: string) => unknown) | null} [get]  null: the allowlist alone
 * @returns {string[]}
 */
function shownOf(reg, preset, get = null) {
  const listed = new Set(Array.isArray(preset?.show) ? preset.show : []);
  const ok = (c, depth = 0) => listed.has(c.id) && !NO_BUTTON.includes(c.id) && !(get && controlBlocked(c.id, get)) && (!c.parent || (depth < 4 && !!reg.control(c.parent) && ok(reg.control(c.parent), depth + 1)));
  return reg.controls.filter((c) => ok(c)).map((c) => c.id);
}

// Lowest first. `season` is read last (it needs the season on screen) but keeps its place; `clock` is where
// time.mode `real` and season.mode `calendar` put the real time: above the preset, below the visitor.
// The address beats memory, under every preset: a shared link must show what it says, whatever this browser remembers.
// A RUNNING TOUR WINS OVER THE EDITOR (delivery 4; the one exception to "layer 9 is on top"): while a tour or a
// preview of one plays, the keys it sets come from it, also over the editor's unsaved value; the tour layer empties
// when it ends and the editor's value is back.
const RANK = { default: 0, package: 1, season: 2, bundle: 3, preset: 4, placement: 5, clock: 6, stored: 7, url: 8, visitor: 9, editor: 10, tour: 11 };
// VIS-226: the layers the map's owner writes (not the visitor's link, memory or press)
const OWNER = ['bundle', 'preset', 'placement', 'editor'];
// The settings whose opening value a `*.mode` setting hands to the visitor's link and remembered choice, or not.
const MODE_OF = { 'time.date': 'time.mode', 'time.hour': 'time.mode', 'season.current': 'season.mode', 'lang.current': 'lang.mode' };
// Address parameters whose text is not the value as written (main.js, config.js, render.js on main 23dc2a0).
const PARAM = {
  mode: (v) => (v === 'night' ? 20 : undefined),
  hour: (v) => Number(v), // an empty ?hour= is midnight, as on main
  aurora: (v) => (v === 'on' ? true : v === 'off' ? false : undefined),
  auroraBearing: (v) => (v ? ((Number(v) % 360) + 360) % 360 : undefined),
  auroraAuto: (v) => v === '1', // main (WS-47): 1 = on, any other value = off
  season: (v) => v.replace(/[^a-z]/g, ''),
  quality: (v) => (v === 'auto' ? undefined : v), // not a tier: the stored choice, else the device, decides
};
const TRUE = ['1', 'true', 'on'], FALSE = ['0', 'false', 'off'];
const fromText = (e, v) => (e.type === 'number' ? (v ? Number(v) : undefined) : e.type === 'bool' ? (TRUE.includes(v) ? true : FALSE.includes(v) ? false : undefined)
  : ['enum', 'place', 'monthDay', 'color'].includes(e.type) ? v : undefined);

function pairs(url) {
  const out = new Map();
  const it = url && typeof url === 'object' ? (typeof url[Symbol.iterator] === 'function' ? url : Object.entries(url)) : [];
  for (const [k, v] of it) if (!out.has(k) && v != null) out.set(k, String(v));
  return out;
}

/** The short date a resort asked for: `?date=MMDD` or `?date=MMDDHHMM`, month first; its time feeds `?hour` unless the link names one. */
function shortDate(url) {
  const m = /^(\d{2})(\d{2})(?:(\d{2})(\d{2}))?$/.exec(url.get('date') || ''), md = m && monthDay(`${m[1]}-${m[2]}`);
  if (!md) return url;
  url.set('date', md);
  if (m[3] && !url.has('hour') && +m[4] < 60 && +m[3] + +m[4] / 60 <= 24) url.set('hour', String(Math.round((+m[3] + +m[4] / 60) * 100) / 100));
  return url;
}

/** The wall clock at the site: `{ md: "MM-DD", hour }` for an epoch time and `site.tz` (a zone name or `+02:00`). */
function clockAt(now, tz) {
  if (!Number.isFinite(now)) return null;
  const fixed = /^([+-])(\d{2}):(\d{2})$/.exec(tz || '+00:00');
  let p;
  if (fixed) {
    const d = new Date(now + (fixed[1] === '-' ? -1 : 1) * (+fixed[2] * 60 + +fixed[3]) * 60000);
    p = { month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() };
  } else {
    try {
      p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })
        .formatToParts(new Date(now)).map((x) => [x.type, +x.value]));
    } catch { return null; } // not a zone this runtime knows: the preset's own time stays
  }
  return { md: monthDay(`${p.month}-${p.day}`), hour: Math.round((p.hour + p.minute / 60) * 100) / 100 };
}

/**
 * Resolves every setting through the layers of contract section 3, lowest first; a higher layer wins per key.
 *
 *  1 registry defaults · 2 the package (resort.json and content.json through `legacy`, then the `x.` settings of
 *  settings.json) · 3 the season patch of the season on screen · 4 the bundle's resort-level values and sections ·
 *  5 the preset · 6 the placement (`placement: true` keys only) · 7 the visitor: remembered values, address
 *  parameters, presses, each only through a shown control · 8 a running tour (`tour: true` keys only) · 9 the editor
 *  (a key a running tour sets comes from the tour while it plays: see RANK).
 *
 * `time.mode`, `season.mode` and `lang.mode` decide where the opening value comes from: `visitor` lets the link
 * and the remembered choice replace the preset's value, `fixed` does not, `real` / `calendar` take the clock
 * (`host.now`, in `site.tz`). What the visitor presses counts in every mode: that is what `show` switches.
 * Pure: the caller passes the parsed address, the storage values and the time in. Values are checked per type at
 * every layer (a bad one is reported in `rejected` and the layer below stays; numbers are brought into range).
 * VIS-226: a `camera.focus` from the owner's layers counts only under camera.rule `focus` (else `not-focus`).
 *
 * @param {Layers} [layers]
 * @returns {ResolveResult}
 */
export function resolve(layers) {
  if (!isObj(layers)) layers = {};
  const reg = typeof layers.registry?.entry === 'function' ? layers.registry : BASE, { resort, content, bundle, preset } = layers;
  const host = isObj(layers.host) ? layers.host : {};
  const ctx = { resort, bundle };
  const values = {}, sources = {}, rejected = [], fromPackage = new Map(), legacy = isLegacy(preset);
  const rank = (layer) => RANK[layer];
  const reject = (key, layer, reason) => { rejected.push({ key, layer, reason }); };
  const focusBy = {}; // VIS-226: every valid camera.focus by layer, so a dropped owner's place falls back to a visitor's
  const set = (e, key, value, layer) => {
    const c = check(e, value, reg, ctx);
    if (!c.ok) return reject(key, layer, 'bad-value');
    if (key === 'camera.focus') focusBy[layer] = c.value;
    if (rank(layer) < rank(sources[key] || 'default')) return;
    values[key] = c.value; sources[key] = layer;
  };
  const put = (patch, layer, allow) => {
    for (const [key, value] of Object.entries(isObj(patch) ? patch : {})) {
      const e = reg.entry(key);
      const why = !e ? 'unknown-key' : allow ? allow(e, value) : null;
      if (why) reject(key, layer, why); else set(e, e.family ? key : e.key, value, layer); // an alias is stored under the current key
    }
  };

  // 1 and 2
  for (const e of reg.settings) {
    if (e.family) continue;
    values[e.key] = clone(e.default); sources[e.key] = e.key.startsWith('x.') ? 'package' : 'default';
    const v = readPackage(e, resort, content);
    fromPackage.set(e, v);
    if (v !== undefined) set(e, e.key, v, 'package');
  }
  // 4: resort-level values, then the sections that are documents of their own (tours are laid over the package's)
  // (delivery 2: a compiled bundle's `settings` also carries the package's settings list; those fields are not values)
  put(valuesOnly(bundle?.settings), 'bundle', (e) => (e.level === 'resort' ? null : 'wrong-level'));
  for (const e of reg.settings) {
    const section = e.bundle && isObj(bundle) ? bundle[e.bundle] : null;
    if (section != null) set(e, e.key, e.overlay && isObj(values[e.key]) && isObj(section) ? { ...values[e.key], ...section } : section, 'bundle');
  }
  // 5 and 6; a value that switches a gated thing on needs its entitlement (the bundle's flags, when it carries any)
  const flags = isObj(bundle?.flags) ? bundle.flags : null;
  const gated = (e, v) => { const g = gateFor(e, v); return flags && g && !flags[g] ? 'gate-off' : null; };
  put(preset?.values, 'preset', (e, v) => (e.level === 'preset' ? gated(e, v) : 'wrong-level'));
  put(layers.placement?.values, 'placement', (e, v) => (e.placement ? gated(e, v) : 'not-placement'));

  // the modes: no control reaches them, so only the preset and the editor's unsaved change can set them
  const mode = (key) => { const e = reg.entry(key), c = check(e, layers.editor?.[key], reg, ctx); return c.ok ? c.value : values[key]; };
  const now = clockAt(host.now, values['site.tz']);
  if (now && mode('time.mode') === 'real') { set(reg.entry('time.date'), 'time.date', now.md, 'clock'); set(reg.entry('time.hour'), 'time.hour', now.hour, 'clock'); }
  if (now && mode('season.mode') === 'calendar') {
    // VIS-128: only a season the preset offers (as the editor's calendar lists them); none offered = any season
    const off = values['season.offered'];
    const hit = values['season.calendar'].find((r) => { const a = monthDay(r.from), b = monthDay(r.to); return a && b && SEASONS.includes(r.season) && (!off.length || off.includes(r.season)) && (a <= b ? now.md >= a && now.md <= b : now.md >= a || now.md <= b); });
    if (hit) set(reg.entry('season.current'), 'season.current', hit.season, 'clock');
  }

  // 7: only through a shown control. VIS-75: `shown` (what the page draws) also leaves out a control the owner's
  // values make useless (the layers so far, with the editor's unsaved value over them: `mode`; never what the visitor
  // presses below); what a link, a remembered value and a press reach stays the allowlist's, as before.
  const listed = shownOf(reg, preset), isShown = new Set(listed), shown = shownOf(reg, preset, mode);
  const reach = new Set(listed.flatMap((id) => reg.control(id).settings));
  // VIS-73: in the editor (`host.ghosts`: the controls the preset hides are drawn, dimmed, and can be pressed) a press
  // reaches the settings of every control; a link and a remembered value still only those of the shown ones
  const pressable = host.ghosts ? new Set(reg.controls.flatMap((c) => c.settings)) : reach;
  const visit = (e, value, layer) => {
    const k = e.key, m = MODE_OF[k];
    const why = m && layer !== 'visitor' && mode(m) !== 'visitor' ? 'fixed-mode'
      : k === 'season.current' && !host.drafts && !values['season.offered'].includes(value) ? 'not-offered'
      : k === 'lang.current' && value !== null && !values['lang.offered'].includes(value) ? 'not-offered'
      : k === 'aurora.on' && layer === 'url' && value === true && values['features.aurora'] === false ? 'feature-off' : null;
    if (why) reject(k, layer, why); else set(e, k, value, layer);
  };
  const url = shortDate(pairs(layers.url)), stored = isObj(layers.stored) ? layers.stored : {};
  const rid = host.resort || resort?.id || '', remembered = [];
  for (const c of reg.controls) {
    const on = isShown.has(c.id);
    for (const p of [...c.url].reverse()) { // the first parameter listed wins (?hour over ?mode=night)
      const e = url.has(p) ? reg.settings.find((s) => s.legacy?.url?.includes(p) && c.settings.includes(s.key)) || (c.settings.length === 1 ? reg.entry(c.settings[0]) : null) : null;
      if (!e) continue;
      // fix/e2e-red-local: a link's aurora placement acts on every resort under the implicit preset (controls.js urlWhenHidden)
      if (!on && !(legacy && c.legacy?.urlWhenHidden)) { reject(e.key, 'url', 'hidden-control'); continue; }
      const v = Object.hasOwn(PARAM, p) ? PARAM[p](url.get(p)) : fromText(e, url.get(p));
      if (v !== undefined) visit(e, v, 'url');
    }
    // today's storage keys count under the implicit legacy presets alone
    for (const s of legacy ? c.store : []) {
      const raw = stored[s.replace('<resort>', rid)];
      if (typeof raw !== 'string') continue;
      for (const e of reg.settings.filter((x) => x.legacy?.store?.includes(s))) {
        const v = readStore(e, raw, resort);
        if (v !== undefined) remembered.push([e, v, on]);
      }
    }
  }
  if (!legacy && preset) {
    // under a real preset: `mms:<map>:<preset id>:<key>` (the preset's slug is read too), JSON
    for (const e of reg.settings) {
      const raw = [preset.id, preset.slug].map((p) => stored[`mms:${host.map || rid}:${p}:${e.key}`]).find((x) => typeof x === 'string');
      if (raw === undefined || e.family) continue;
      try { remembered.push([e, JSON.parse(raw), reach.has(e.key)]); } catch { reject(e.key, 'stored', 'bad-value'); }
    }
  }
  for (const [e, v, on] of remembered) { if (on) visit(e, v, 'stored'); else reject(e.key, 'stored', 'hidden-control'); }
  for (const [key, value] of Object.entries(isObj(layers.visitor) ? layers.visitor : {})) {
    const e = reg.entry(key);
    if (!e || !pressable.has(e.key)) reject(key, 'visitor', e ? 'hidden-control' : 'unknown-key'); else visit(e, value, 'visitor');
  }
  // 9 and 8 (in the order of RANK: a running tour over the editor)
  put(layers.editor, 'editor');
  put(layers.tour, 'tour', (e) => (e.tour ? null : 'not-tour'));

  // 3: the patch of the season now on screen, read through the same `legacy` paths as layer 2
  const patch = layers.seasonal !== undefined ? layers.seasonal : at(resort, `seasonal.${values['season.current']}`);
  if (isObj(patch) && isObj(resort)) {
    const inSeason = seasonMerged(resort, patch);
    for (const e of reg.settings) {
      if (e.family || e.key === 'season.current' || e.legacy?.file === 'content') continue;
      const v = readPackage(e, inSeason, content), was = fromPackage.get(e);
      if (v !== undefined && v !== was && JSON.stringify(v) !== JSON.stringify(was)) set(e, e.key, v, 'season');
    }
  }
  // a gate that is off: whatever the layers left (the default of a gated switch is ON), the gated thing is off
  for (const e of reg.settings) if (!e.family && gated(e, values[e.key])) values[e.key] = offOf(e);
  // VIS-226: "where the map opens" (camera.view) is the one way in. A place the map's owner set (bundle, preset,
  // placement, editor) counts only under camera.rule `focus`, where the camera stays on it; under any other rule it
  // is dropped (old presets still load). A place the visitor chose (link, memory, press) keeps working.
  if (values['camera.rule'] !== 'focus' && OWNER.includes(sources['camera.focus'])) {
    reject('camera.focus', sources['camera.focus'], 'not-focus');
    const own = Object.keys(focusBy).filter((l) => !OWNER.includes(l) && rank(l) > rank('season')).sort((a, b) => rank(b) - rank(a))[0];
    values['camera.focus'] = own ? focusBy[own] : clone(reg.entry('camera.focus')?.default ?? null); sources['camera.focus'] = own || 'default';
  }
  return { values, shown, sources, rejected };
}
